import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, MessageSquare } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import NotificationBell from "@/components/NotificationBell";

export default function DashboardShell({ title, tabs, active, onTab, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col border-r border-border bg-card min-h-screen sticky top-0" data-testid="dashboard-sidebar">
          <Link to="/" className="flex items-center gap-2 px-6 h-[72px] border-b border-border">
            <Wordmark iconClass="h-8 w-auto" textClass="text-base" />
          </Link>
          <nav className="flex-1 p-4 space-y-1">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => onTab(t.key)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${active === t.key ? "bg-accent text-white" : "hover:bg-secondary"}`} data-testid={`tab-${t.key}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-border">
            <div className="px-2 mb-3">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
            <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors" data-testid="dashboard-logout">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </aside>

        {/* Content */}
        <main className="min-h-screen min-w-0">
          {/* Desktop top bar */}
          <div className="hidden lg:flex items-center justify-end gap-1 h-16 px-8 border-b border-border">
            <NotificationBell />
            <Link to="/messages" className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid="dashboard-messages"><MessageSquare className="h-5 w-5" /></Link>
          </div>

          {/* Mobile top bar */}
          <div className="lg:hidden glass sticky top-0 z-30 border-b border-border h-16 flex items-center justify-between px-5">
            <Link to="/" className="flex items-center gap-2"><Wordmark iconClass="h-8 w-auto" textClass="text-base" /></Link>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <Link to="/messages" className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid="mobile-dashboard-messages"><MessageSquare className="h-5 w-5" /></Link>
              <button onClick={logout} data-testid="mobile-dashboard-logout"><LogOut className="h-5 w-5" /></button>
            </div>
          </div>

          {/* Mobile tabs */}
          <div className="lg:hidden flex gap-2 overflow-x-auto px-5 py-3 border-b border-border">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => onTab(t.key)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === t.key ? "bg-accent text-white" : "bg-secondary"}`} data-testid={`mtab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-5 sm:p-8 max-w-5xl">
            <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-6">{title}</h1>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
